import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  broadcastNotification,
  listAdminNotifications,
  revokeNotification,
} from "@/lib/system-notifications.functions";

export const Route = createFileRoute("/_authenticated/admin/notifications")({
  component: AdminNotifications,
});

function AdminNotifications() {
  const qc = useQueryClient();
  const fetchList = useServerFn(listAdminNotifications);
  const send = useServerFn(broadcastNotification);
  const revoke = useServerFn(revokeNotification);

  const { data } = useQuery({
    queryKey: ["admin-notifications"],
    queryFn: () => fetchList(),
  });

  const [scope, setScope] = useState<"platform_all" | "account" | "user">(
    "platform_all",
  );
  const [accountId, setAccountId] = useState("");
  const [userId, setUserId] = useState("");
  const [severity, setSeverity] = useState<"info" | "warning" | "critical">(
    "info",
  );
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [actionUrl, setActionUrl] = useState("");
  const [actionLabel, setActionLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function onSend() {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSubmitting(true);
    try {
      await send({
        data: {
          scope,
          account_id: scope === "account" ? accountId.trim() : null,
          user_id: scope === "user" ? userId.trim() : null,
          severity,
          category: "admin_broadcast",
          title: title.trim(),
          body: body.trim() || null,
          action_url: actionUrl.trim() || null,
          action_label: actionLabel.trim() || null,
        },
      });
      toast.success("Notification sent");
      setTitle("");
      setBody("");
      setActionUrl("");
      setActionLabel("");
      qc.invalidateQueries({ queryKey: ["admin-notifications"] });
      qc.invalidateQueries({ queryKey: ["system-notifications"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Send failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function onRevoke(id: string) {
    if (!confirm("Revoke this notification? Recipients will no longer see it."))
      return;
    try {
      await revoke({ data: { notificationId: id } });
      toast.success("Revoked");
      qc.invalidateQueries({ queryKey: ["admin-notifications"] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Revoke failed");
    }
  }

  return (
    <div className="space-y-6">
      <Card className="p-5">
        <h2 className="text-lg font-semibold">Broadcast notification</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Delivered to the in-app bell. Use sparingly for outages, releases, and
          critical changes.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label>Scope</Label>
            <Select value={scope} onValueChange={(v) => setScope(v as any)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="platform_all">All users (platform)</SelectItem>
                <SelectItem value="account">Single account</SelectItem>
                <SelectItem value="user">Single user</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Severity</Label>
            <Select
              value={severity}
              onValueChange={(v) => setSeverity(v as any)}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {scope === "account" && (
            <div className="md:col-span-2">
              <Label>Account ID</Label>
              <Input
                className="mt-1 font-mono text-xs"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                placeholder="UUID"
              />
            </div>
          )}
          {scope === "user" && (
            <div className="md:col-span-2">
              <Label>User ID</Label>
              <Input
                className="mt-1 font-mono text-xs"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="UUID"
              />
            </div>
          )}
          <div className="md:col-span-2">
            <Label>Title</Label>
            <Input
              className="mt-1"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={160}
              placeholder="Scheduled maintenance tonight 22:00 UTC"
            />
          </div>
          <div className="md:col-span-2">
            <Label>Body (optional)</Label>
            <Textarea
              className="mt-1 min-h-24"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={2000}
              placeholder="Brief details users should know."
            />
          </div>
          <div>
            <Label>Action URL (optional)</Label>
            <Input
              className="mt-1"
              value={actionUrl}
              onChange={(e) => setActionUrl(e.target.value)}
              placeholder="/settings/billing"
            />
          </div>
          <div>
            <Label>Action label (optional)</Label>
            <Input
              className="mt-1"
              value={actionLabel}
              onChange={(e) => setActionLabel(e.target.value)}
              placeholder="View details"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={onSend} disabled={submitting}>
            {submitting ? "Sending…" : "Send notification"}
          </Button>
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="text-lg font-semibold">Recent (100)</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">When</th>
                <th className="py-2 pr-3">Scope</th>
                <th className="py-2 pr-3">Severity</th>
                <th className="py-2 pr-3">Title</th>
                <th className="py-2 pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(data?.items ?? []).map((n) => (
                <tr key={n.id}>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">
                    {new Date(n.created_at).toLocaleString()}
                  </td>
                  <td className="py-2 pr-3">
                    <Badge variant="secondary" className="text-xs">
                      {n.scope}
                    </Badge>
                  </td>
                  <td className="py-2 pr-3">
                    <Badge
                      variant={
                        n.severity === "critical"
                          ? "destructive"
                          : n.severity === "warning"
                            ? "default"
                            : "secondary"
                      }
                      className="text-xs"
                    >
                      {n.severity}
                    </Badge>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="font-medium">{n.title}</div>
                    {n.body && (
                      <div className="line-clamp-1 text-xs text-muted-foreground">
                        {n.body}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRevoke(n.id)}
                    >
                      Revoke
                    </Button>
                  </td>
                </tr>
              ))}
              {(data?.items ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="py-6 text-center text-sm text-muted-foreground"
                  >
                    No notifications sent yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
